import { ICardConfiguration } from '~/client/src/shared/components/ResourceCardList/types'
import { IResourceDefinition } from '~/client/src/shared/resources/definitions/types'

/**
 * Whether a card's layout comes from the resource type's own definition.
 *
 * On (the default), a resource template's `cardConfiguration` is ignored entirely and the
 * templates query is skipped: that config is seeded server-side, read-only over the API,
 * and arrives on an unordered list, so reading it made a card's layout depend on which
 * template happened to come back first.
 *
 * Turn off to restore the template override. The call sites read this to decide whether to
 * fetch templates at all, so flipping it also brings the query — and its readiness cost —
 * back. Kept as an escape hatch for a project that needs per-template card layouts before
 * the configuration has a real write path.
 */
export const USE_DEFAULT_CARD_CONFIG = true

interface ITemplateCardConfig {
  displayProperties?: string[] | null
  subtitleProperty?: string | null
}

/**
 * A resource type's card layout.
 *
 * Callers pass a template only when USE_DEFAULT_CARD_CONFIG is off; with none, the
 * definition alone decides. One resolver for both surfaces that render cards — the shared
 * desktop hook and the mobile provider — so the two cannot drift apart.
 */
export function buildResourceCardConfiguration(
  resourceDefinition: IResourceDefinition | undefined,
  template?: { cardConfiguration?: ITemplateCardConfig | null } | null,
): ICardConfiguration {
  const definitionDisplayProperties =
    resourceDefinition?.card.defaultDisplayPropertyKeys || []
  const definitionSubtitle = resourceDefinition?.card.subtitleKey || null

  const templateConfig = template?.cardConfiguration
  const templateDisplayProperties = templateConfig?.displayProperties || []

  // An empty template list means "not configured", so the definition still wins.
  const displayProperties = templateDisplayProperties.length
    ? templateDisplayProperties
    : definitionDisplayProperties

  return {
    subtitleProperty: templateConfig
      ? templateConfig.subtitleProperty || definitionSubtitle
      : definitionSubtitle,
    // Exactly the chosen keys, in order. Fields sourced from a linked resource
    // (sensor_name, equipment_name, …) are named in the list like any other, so a card
    // shows what its definition says and nothing more.
    displayProperties,
  }
}
